<template>
    <div class="add-to-sequence-process-modal">
        <ProcessModal
            :is-open="isOpen"
            :title="$t('funnel.contact.flows.start.add.to.sequence')"
            :summary-text="$t('funnel.contact.flow.action.summary')"
            show-save
            :process-tip-text="$t('funnel.contact.flows.start.remove.from.sequence.warning')"
            :process-tip-link="toolTipLink"
            @close-modal="closeModal"
            @update-config="updateConfig"
        >
            <template #processParameters>
                <DsRadioGroup
                    v-model="selectedOptionType"
                    :options="addRemoveOptions"
                    class="add-remove-radio-group"
                    data-qa="add-remove-radio-group-qa"
                    @input="toggleAddRemove"
                />

                <div v-if="showAddSequenceOption">
                    <DsMultiselect
                        v-model="selectedCampaign"
                        data-qa="selected-campaign-input"
                        :options="publishedCampaign"
                        bind-value-only
                        :placeholder="$t('funnel.contact.flow.add.to.sequence.campaign.dropdown')"
                        :label="$t('funnel.contact.flow.add.to.sequence.campaign.dropdown')"
                        :force-invalid="invalidCampaign"
                        required
                        @input="loadCampaignSequences"
                    >
                        <template #help>
                            <span v-if="invalidCampaign" data-qa="invalid-campaign-id">
                                {{ $t('funnel.contact.flow.add.to.sequence.required') }}
                            </span>
                        </template>
                    </DsMultiselect>
                    <DsMultiselect
                        v-model="selectedSequence"
                        data-qa="selected-sequence-input"
                        :class="{'selected-sequence-input': shouldDisplayCss}"
                        :readonly="!campaignSequencesAvailable"
                        :options="campaignSequences"
                        bind-value-only
                        :placeholder="$t('funnel.contact.flow.select.sequence.dropdown')"
                        :label="$t('funnel.contact.flow.select.sequence.dropdown')"
                        :force-invalid="invalidSequence"
                        required
                        @input="cleanSequenceErrors"
                    >
                        <template #help>
                            <span>
                                {{ $t('funnel.contact.flow.select.sequence.dropdown.info') }}
                            </span>
                            <span v-if="invalidSequence" data-qa="invalid-sequence-id">
                                {{ $t('funnel.contact.flow.add.to.sequence.required') }}
                            </span>
                        </template>
                    </DsMultiselect>

                    <DsCheckbox
                        v-if="!!selectedCampaign && !!selectedSequence"
                        v-model="checkboxRemoveContacts"
                        data-qa="checkbox-remove-contacts"
                        class="checkbox-remove-contacts"
                        :label="$t('funnel.contact.flows.start.remove.from.sequence.label')"
                    />
                </div>
            </template>
        </ProcessModal>
    </div>
</template>

<script>
import ProcessModal from '../processModal/ProcessModal.vue';

import {
    getCampaignSequences,
    getPublishedCampaigns,
} from './add.to.sequence.api';

export const ASSIGN_ADD_REMOVE_SEQUENCE = {
    addSequence: 'addSequence',
    removeSequence: 'removeSequence',
};

export default {
    ASSIGN_ADD_REMOVE_SEQUENCE,
    components: {
        ProcessModal,
    },
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        appInfo: {
            type: Object,
            required: true,
        },
        cell: {
            type: Object,
            default: () => ({
                value: {
                    funnelId: '',
                    flowId: '',
                    name: '',
                    removeFromSequence: '0',
                },
            }),
        },
    },
    emits: ['update-cell-value', 'close-modal'],
    data() {
        return {
            selectedCampaign: '',
            selectedSequence: '',
            publishedCampaign: [],
            campaignSequences: [],
            invalidCampaign: false,
            invalidSequence: false,
            removeFromSequence: false,
            selectedOptionType: '',
            addRemoveOptions: [
                { value: ASSIGN_ADD_REMOVE_SEQUENCE.addSequence, label: this.$t('funnel.contact.flows.start.add.to.sequence.option.add') },
                { value: ASSIGN_ADD_REMOVE_SEQUENCE.removeSequence, label: this.$t('funnel.contact.flows.start.add.to.sequence.option.remove') },
            ],

        };
    },
    computed: {
        checkboxRemoveContacts: {
            set(newValue) {
                this.removeFromSequence = newValue ? '1' : '0';
            },
            get() {
                return this.removeFromSequence === '1';
            },
        },
        isSpaApp() {
            return this.appInfo?.appEditionFlags?.spaApp;
        },
        toolTipLink() {
            return this.isSpaApp ? this.$t('funnel.contact.flow.add.to.sequence.tip.url.keap')
                : this.$t('funnel.contact.flow.add.to.sequence.tip.url.classic');
        },
        campaignSequencesAvailable () {
            return this.campaignSequences?.length > 0;
        },
        isAddSequenceEnabled() {
            return this.selectedOptionType === this.$options.ASSIGN_ADD_REMOVE_SEQUENCE.addSequence;
        },
        isRemoveSequenceEnabled() {
            return this.selectedOptionType === this.$options.ASSIGN_ADD_REMOVE_SEQUENCE.removeSequence;
        },
        showAddSequenceOption() {
            if (this.isAddSequenceEnabled) {
                return true;
            }

            return false;
        },
        shouldDisplayCss() {
            return this.selectedCampaign && this.selectedSequence;
        },
    },
    async mounted() {
        await this.loadPublishedCampaign();
        await this.loadConfig();
    },
    methods: {
        async loadConfig() {
            const { value } = this.cell;

            if (!value) {
                return;
            }

            this.removeFromSequence = value.removeFromSequence;

            if (this.showAddOrRemoveSeq(value)) {
                this.selectedOptionType = this.$options.ASSIGN_ADD_REMOVE_SEQUENCE.removeSequence;
            } else {
                this.selectedOptionType = this.$options.ASSIGN_ADD_REMOVE_SEQUENCE.addSequence;

                const funnelId = value.funnelId?.toString() || '';

                if (funnelId === '-1') {
                    this.selectedCampaign = '';
                    this.selectedSequence = '';
                } else {
                    this.selectedCampaign = funnelId;

                    if (this.selectedCampaign) {
                        await this.loadCampaignSequences();
                    }
                    this.selectedSequence = value.flowId?.toString() || '';
                }
            }
        },
        showAddOrRemoveSeq({ funnelId, flowId, removeFromSequence } = {}) {
            if ((!funnelId?.trim() || funnelId === '-1')
                && (!flowId?.trim() || flowId === '-1')
                && removeFromSequence === '1') {
                return true;
            }

            return false;
        },
        async loadPublishedCampaign() {
            try {
                this.publishedCampaign = await getPublishedCampaigns();
            } catch (error) {
                this.$error({ message: this.$t('funnel.contact.flow.add.to.sequence.campaign.error') });
            }
        },
        async loadCampaignSequences(value) {
            this.cleanCampaignErrors(value);
            this.campaignSequences = [];
            this.selectedSequence = '';

            try {
                this.campaignSequences = await getCampaignSequences(this.selectedCampaign);
            } catch (error) {
                this.$error({ message: this.$t('funnel.contact.flow.add.to.sequence.load.sequence.error') });
            }
        },
        async updateConfig() {
            if (this.isValid()) {
                await this.saveCellValue();
            }
        },
        cleanCampaignErrors (value) {
            if (this.invalidCampaign && value) {
                this.setIsValidCampaign();
            }
        },
        cleanSequenceErrors (value) {
            if (this.invalidSequence && value) {
                this.setIsValidSequence();
            }
        },
        setIsValidCampaign() {
            this.invalidCampaign = this.selectedCampaign === ''
                || this.selectedCampaign === null
                || this.selectedCampaign === '-1';
        },
        setIsValidSequence() {
            this.invalidSequence = this.selectedSequence === ''
                || this.selectedSequence === null
                || this.selectedSequence === '-1';
        },
        isValid() {
            if (this.isAddSequenceEnabled) {
                this.setIsValidCampaign();
                this.setIsValidSequence();

                return !this.invalidCampaign && !this.invalidSequence;
            }

            return true;
        },
        saveCellValue() {
            const cell = { ...this.cell };

            cell.value = cell.value || {};

            if (this.isRemoveSequenceEnabled) {
                cell.value.removeFromSequence = '1';
                cell.value.funnelId = '';
                cell.value.flowId = '';
            } else {
                cell.value.removeFromSequence = this.removeFromSequence;
                cell.value.funnelId = this.selectedCampaign;
                cell.value.flowId = this.selectedSequence;
            }
            cell.value.name = this.getLabel();

            this.$emit('update-cell-value', { cell });
        },
        getSelectedCampaignName() {
            return this.publishedCampaign.find((campaign) => campaign.value === this.selectedCampaign)?.label;
        },
        getSelectedSequenceName() {
            return this.campaignSequences.find((sequence) => sequence.value === this.selectedSequence)?.label;
        },
        getLabel() {
            if (this.isRemoveSequenceEnabled) {
                return this.$t('funnel.campaign.add.to.sequence.remove');
            }

            return `${this.$t('funnel.campaign.add.to.sequence.label')} ${this.getSelectedCampaignName()} > ${this.getSelectedSequenceName()}`;
        },
        closeModal() {
            this.$emit('close-modal');
        },
        toggleAddRemove() {
            if (this.isRemoveSequenceEnabled) {
                this.invalidCampaign = false;
                this.invalidSequence = false;
            }
        },
    },
};
</script>
<style lang="scss">

div.add-to-sequence-process-modal {
    div.dropdown-reference > div > label {
        padding-top: 0;
    }
    div.dropdown-reference > div.assistive-text {
        display: flex;
        flex-direction: column;
    }
}

.process-configuration[data-v-19e77390] {
    margin-top: 0;
}

</style>

<style lang="scss" scoped>
.add-remove-radio-group {
    display: inline-flex;
    gap: $spacing-250;
    margin-bottom: $spacing-125;
}

.selected-sequence-input {
    margin-bottom: 0;
}

</style>
